import {
  useEffect,
  useState,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
  Chip,
  Switch,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { FiArrowLeft, FiCalendar, FiClock, FiEye, FiSend } from "react-icons/fi";
import { MdPets } from "react-icons/md";
import PetsIcon from "@mui/icons-material/Pets";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import NotesIcon from "@mui/icons-material/Notes";
import { httpService } from "../../services/httpService";
import { tokenDecode } from "../../utils/helper";
import { toast } from "react-toastify";
import React from "react";
import { parse, isBefore } from "date-fns";
import PetCard from "./PetCard";
import { useToken } from "../../hooks/token/useToken";
import AddNewPet from "./AddNewPet";
import ServiceDrawer from "../../components/Service/ServiceDrawer";
import { createPortal } from "react-dom";
import { useProfile } from "../../context/ProfileContext";
import { useAllowPastBooking } from "../../hooks/useAllowPastBooking";

// Helper to convert 24-hour "HH:mm" or "HH:mm:ss" time string ➜ locale 12-hour e.g. "8:15 AM"
const convertTo12Hour = (time24) => {
  if (!time24) return "";

  // If already contains AM/PM (case insensitive), normalize to uppercase and return
  if (/\b(am|pm)$/i.test(time24.trim())) {
    // Normalize to proper case: "4:15 pm" -> "4:15 PM"
    return time24.replace(/\s*am\s*$/i, " AM").replace(/\s*pm\s*$/i, " PM");
  }

  // Handle 24-hour format "HH:MM" or "HH:MM:SS"
  const parts = time24.split(":");
  if (parts.length < 2) return time24;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1].substring(0, 2);
  if (isNaN(hours)) return time24;
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
};

// Helper functions (place these outside your component or inside)
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [time, modifier] = timeStr.trim().split(" ");
  let [hours, minutes] = time.split(":").map(Number);
  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

const parseDurationToMinutes = (durationStr) => {
  if (!durationStr || typeof durationStr !== "string") return 0;
  const lower = durationStr.toLowerCase();
  let total = 0;
  const hourMatch = lower.match(/(\d+)\s*hour/);
  const minuteMatch = lower.match(/(\d+)\s*minute/);
  if (hourMatch) total += parseInt(hourMatch[1]) * 60;
  if (minuteMatch) total += parseInt(minuteMatch[1]);
  return total;
};

// Helper to format minutes into the same "en-IN" time string
const formatTimeSlot = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return new Date(0, 0, 0, hours, minutes).toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  });
};

// ---------- Forms Helpers ----------
const normalizeConsentName = (value = "") =>
  String(value).trim().toLowerCase().replace(/\s+/g, " ");

const getFormRequirement = (form = {}) => {
  const req = form?.requirement;
  if (req === "mandatory") return "mandatory";
  if (typeof form.requirement === "boolean") {
    return form.requirement ? "mandatory" : "optional";
  }
  return "optional";
};

/**
 * Maps a human-readable service_type_name to the API's service category key.
 * The API's `services` field uses identifiers like "grooming", "pet_vet", etc.
 */
const getServiceCategoryKey = (serviceTypeName = "") => {
  const normalized = String(serviceTypeName).trim().toLowerCase();
  if (normalized.includes("grooming")) return "grooming";
  if (normalized.includes("vet") || normalized.includes("pet_vet"))
    return "pet_vet";
  if (normalized.includes("boarding") || normalized.includes("resort"))
    return "boarding";
  if (normalized.includes("day care") || normalized.includes("daycare"))
    return "day_care";
  if (normalized.includes("training")) return "training";
  if (normalized.includes("shop")) return "pet_shop";
  // fallback: return as-is (lowercase, underscored)
  return normalized.replace(/\s+/g, "_");
};

// ---------- Main Component ----------
const NewServiceDrawer = React.forwardRef(
  (
    {
      isOpen,
      onClose,
      onUpdate,
      clientId,
      itemData,
      orderId,
      fromCalendar = false,
      selectedSlotInfo,
      renderAsContent = false,
      editMode = false,
      onRefreshData = null,
      appointmentStatusRef = null,
      serviceTypeId = null,
      onPetFormStateChange = null,
      isPetDetails = false,
      existingAppointments = [],
      checkEmployeeDoubleBooking = null,
      setSaveEnquiryClicked = null,
    },
    ref
  ) => {
    const [services, setServices] = useState([]);
    const servicesRequestRef = useRef(0);
    const [staffs, setStaffs] = useState([]);
    const { data } = tokenDecode();
    const { isEmployee } = useToken();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [isPetDisabled, setIsPetDisabled] = useState(isPetDetails);
    const [showServiceNotes, setShowServiceNotes] = useState(false);
    const [showServiceDetailsModal, setShowServiceDetailsModal] =
      useState(false);
    // Minimum selectable date is today
    const todayStr = new Date().toISOString().split("T")[0];
    const { profileData } = useProfile();
    const sizeCoatSelection = profileData?.sizeCoatSelection || "size_and_coat";
    const isSizeRequired = sizeCoatSelection !== "only_coat";
    const isCoatRequired = sizeCoatSelection !== "only_size";

    const { allowPastBooking, maxPastDays, minDate, minDateStr } =
      useAllowPastBooking();

    // Helper: determine if a given time slot is in the past relative to now
    const isPastTimeSlot = (slotLabel, selectedDate) => {
      if (allowPastBooking) return false;
      if (!selectedDate) return false; // No date selected yet
      try {
        const slotDate = parse(
          `${selectedDate} ${slotLabel}`,
          "yyyy-MM-dd h:mm a",
          new Date()
        );
        return isBefore(slotDate, new Date());
      } catch {
        return false;
      }
    };

    const [showNewPetForm, setShowNewPetForm] = useState(false);
    const [isEditPet, setIsEditPet] = useState(false);
    const [pets, setPets] = useState([]);
    const [newPet, setNewPet] = useState({
      type: "",
      name: "",
      gender: "",
      size: "",
      breed: "",
      age: "",
      birth_date: "",
      behavior_notes: "",
      tags: [],
    });
    const [serviceDetailData, setServiceDetailData] = useState([]);

    // ✅ FIX: Explicitly track base price (mrp/basic), sale price, and the
    // currently effective price used for display + totals. Keeping these as
    // separate fields prevents the discounted value from being mistaken for
    // the base value when re-saving from edit mode.
    const [petService, setPetService] = useState({
      pet_id: "",
      size: "",
      breed: "",
      pet_type: "",
      appointment_date: "",
      appointment_start_time: "",
      service_id: "",
      duration: "",
      price: 0, // effective price (sale if discounted)
      basic_price: 0, // base price from service detail
      mrp_price: 0, // original MRP (never overwritten by discount)
      sale_price: 0, // discounted price
      discount_percentage: 0,
      discount_amount: 0,
      discount: 0, // manual discount amount applied at booking level
      gst: 0,
      gst_amount: 0,
      totalAmount: 0,
      appointment_to: "",
      tax_percentage: 0,
      notes: "",
      unit_price: 0,
      price_surcharge: [],
    });

    // State for tracking if this is from queue drop with original time
    const [isFromQueueWithOriginalTime, setIsFromQueueWithOriginalTime] =
      useState(false);
    const [originalQueueTime, setOriginalQueueTime] = useState(null);
    const [petDetails, setPetDetails] = useState([]);
    const [editDataFetched, setEditDataFetched] = useState(false);
    const [userManuallyChangedPet, setUserManuallyChangedPet] = useState(false);
    const [staffAvailableMessage, setStaffAvailableMessage] = useState("");
    const [businessHours, setBusinessHours] = useState(null);
    const [servicesd, setServicesd] = useState({});
    const [showServiceDrawer, setShowServiceDrawer] = useState(false);

    // ---------- Forms State (integrated) ----------
    const [availableForms, setAvailableForms] = useState([]);
    const [selectedForms, setSelectedForms] = useState([]);
    const [formInputValue, setFormInputValue] = useState("");
    const [loadingForms, setLoadingForms] = useState(false);
    const [sendingForms, setSendingForms] = useState(false);
    const [sentFormNames, setSentFormNames] = useState([]);
    const preselectedFormIdsRef = React.useRef(new Set());
    const autoSelectedPetRef = useRef(false);

    // ---------- Forms Functions ----------
    const fetchForms = async () => {
      if (!clientId || !petService.pet_id) {
        setAvailableForms([]);
        setSelectedForms([]);
        return;
      }

      setLoadingForms(true);
      try {
        // ✅ PRIORITY 1: use service category ID from selected service (if any)
        const categoryId = serviceDetailData?.service_category?._id;
        // ✅ PRIORITY 2: use serviceTypeId prop (if provided)
        // ✅ PRIORITY 3: use derived key from service_type_name or fallback to "grooming"
        const serviceCategoryKey = getServiceCategoryKey(
          petService.service_type_name || "grooming"
        );
        const serviceIdentifier =
          categoryId || serviceTypeId || serviceCategoryKey;

        const params = new URLSearchParams({
          clientId,
          petId: petService.pet_id,
          serviceName: serviceIdentifier,
        });

        const res = await httpService.get(
          `/consentForm/getavailableFormsForBooking?${params.toString()}`
        );

        const formsArray = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];

        const normalizedForms = formsArray.map((f) => ({
          ...f,
          _id: f.form_id,
          form_name: f.form_name,
        }));

        // ✅ Client-side filter: keep forms that match the service identifier or category key
        const filteredForms = normalizedForms.filter((f) => {
          if (!Array.isArray(f.services) || f.services.length === 0)
            return true;
          return f.services.some((s) => {
            const str = String(s).trim();
            return (
              str === String(serviceIdentifier) ||
              str.toLowerCase() === serviceCategoryKey
            );
          });
        });

        setAvailableForms(filteredForms);

        // Mandatory forms
        const mandatoryForms = filteredForms.filter(
          (f) => f.requirement === "mandatory"
        );
        const mandatoryIds = new Set(mandatoryForms.map((f) => f._id));

        // Preselected from order (edit mode)
        const preselectedIds = new Set([
          ...(Array.isArray(itemData?.form_ids) ? itemData.form_ids : []),
          ...Array.from(preselectedFormIdsRef.current || new Set()),
        ]);

        const allSelectedIds = new Set([...mandatoryIds, ...preselectedIds]);
        const selected = filteredForms.filter((f) => allSelectedIds.has(f._id));

        setSelectedForms(selected);
      } catch (error) {
        console.error("❌ Error fetching booking forms:", error);
        setAvailableForms([]);
        setSelectedForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    useEffect(() => {
      if (isOpen && clientId && petService.pet_id) {
        fetchForms();
      } else {
        setAvailableForms([]);
        setSelectedForms([]);
      }
    }, [
      isOpen,
      clientId,
      petService.pet_id,
      serviceDetailData, // re-fetch when service details load (to get category ID)
      serviceTypeId, // re-fetch if prop changes
    ]);

    const fetchSentForms = async () => {
      const resolvedClientId =
        clientId ||
        itemData?.customer_id ||
        itemData?.vendor_client_id ||
        itemData?.client_id;
      if (!resolvedClientId) return;

      try {
        const res = await httpService.get(
          `/vendorclient/getformdetails/${resolvedClientId}`
        );
        const forms = res.data?.data ?? res.data ?? [];
        const sentNames = forms
          .filter((form) => form.status?.toLowerCase() === "sent")
          .map((form) => form.form_name)
          .filter(Boolean);
        setSentFormNames(sentNames);
      } catch (error) {
        console.error("Error fetching client form details:", error);
      }
    };

    // Auto-fetch forms on open
    useEffect(() => {
      if (isOpen) {
        fetchForms();
        fetchSentForms();
      }
    }, [isOpen, clientId, itemData?._id]);

    // Pre-select forms from order in edit mode
    useEffect(() => {
      if (itemData?.form_ids && Array.isArray(itemData.form_ids)) {
        preselectedFormIdsRef.current = new Set(itemData.form_ids);
      }
    }, [itemData?.form_ids]);

    // ---------- End Forms ----------

    useEffect(() => {
      // Only run when the drawer is open
      if (!isOpen) return;

      // Only auto-select once per session
      if (autoSelectedPetRef.current) return;

      // Don't override an already selected pet
      if (petService.pet_id) return;

      // Wait until the pets list is available
      if (!pets || pets.length === 0) return;

      // Consider only active pets (matching the dropdown filter)
      const activePets = pets.filter(
        (pet) => pet.status?.toLowerCase() !== "inactive"
      );

      if (activePets.length === 1) {
        const singlePet = activePets[0];
        setPetService((prev) => ({ ...prev, pet_id: singlePet._id }));
        fetchPetDetails(singlePet._id);
        autoSelectedPetRef.current = true; // prevent future auto‑selection
      }
    }, [isOpen, pets, petService.pet_id]);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      closePetForm: () => {
        setShowNewPetForm(false);
      },
      resetFormData: () => {
        resetFormData();
      },
    }));

    const shouldDisableDate = (date) => {
      if (!businessHours) return false;

      const iso =
        typeof date === "string" ? date : date.toISOString().split("T")[0];

      const dayName = new Date(iso).toLocaleDateString("en-US", {
        weekday: "long",
      });

      const daySchedule = businessHours.weeklySchedule.find(
        (d) => d.day === dayName
      );

      if (daySchedule && !daySchedule.isOpen) return true;
      const specialDay = businessHours.specialDays?.find(
        (d) => new Date(d.date).toISOString().split("T")[0] === iso
      );
      return specialDay ? !specialDay.isOpen : false;
    };

    // Fetch business hours
    useEffect(() => {
      if (isOpen) {
        const fetchBusinessHours = async () => {
          try {
            const url = "/groominghours/getgroomingbusinesshours";
            const res = await httpService.get(url);
            const data = res.data?.data || res.data || null;
            setBusinessHours(data);
          } catch (err) {
            console.error("Failed to fetch business hours:", err);
            setBusinessHours(null);
          }
        };
        autoSelectedPetRef.current = false;
        fetchBusinessHours();
      }
    }, [isOpen]);

    // Time slot generation
    const time12ToMinutes = (timeStr) => {
      const [time, modifier] = timeStr.trim().split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (modifier === "PM" && hours !== 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;
      return hours * 60 + minutes;
    };

    const generateSlotsFromIntervals = (intervals) => {
      const slots = [];
      intervals.forEach((interval) => {
        let current = time12ToMinutes(interval.start);
        const end = time12ToMinutes(interval.end);
        while (current < end) {
          const h = Math.floor(current / 60);
          const m = current % 60;
          const period = h >= 12 ? "PM" : "AM";
          const displayHour = h % 12 || 12;
          slots.push(
            `${displayHour}:${m.toString().padStart(2, "0")} ${period}`
          );
          current += 15;
        }
      });
      return slots;
    };

    const generateDynamicSlotsForDate = (dateStr) => {
      if (!businessHours || !dateStr) return [];
      const specialDay = businessHours.specialDays?.find(
        (d) => new Date(d.date).toISOString().split("T")[0] === dateStr
      );
      if (specialDay) {
        if (specialDay.isOpen) {
          if (!specialDay.slots || specialDay.slots.length === 0) {
            return generateSlotsFromIntervals(businessHours.timeSlots);
          }
          return generateSlotsFromIntervals(specialDay.slots);
        }
        if (!specialDay.slots || specialDay.slots.length === 0) {
          return [];
        }

        // Slots exist even though isOpen is false → treat them as blocked periods
        // Generate default slots and remove any that fall inside a blocked range
        const defaultSlots = generateSlotsFromIntervals(
          businessHours.timeSlots
        );
        const blockedRanges = specialDay.slots.map((slot) => ({
          start: time12ToMinutes(slot.start),
          end: time12ToMinutes(slot.end),
        }));
        return defaultSlots.filter((slotLabel) => {
          const slotMinutes = time12ToMinutes(slotLabel);
          return !blockedRanges.some(
            (range) => slotMinutes >= range.start && slotMinutes < range.end
          );
        });
      }

      // --- No special day → use weekly schedule ---
      const dayName = new Date(dateStr).toLocaleDateString("en-US", {
        weekday: "long",
      });
      const daySchedule = businessHours.weeklySchedule.find(
        (d) => d.day === dayName
      );
      if (!daySchedule || !daySchedule.isOpen) return [];
      return generateSlotsFromIntervals(businessHours.timeSlots);
    };

    const getNextTimeSlot = () => {
      if (!businessHours) return "";
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const todayStr = now.toISOString().split("T")[0];
      const dayName = now.toLocaleDateString("en-US", { weekday: "long" });
      const daySchedule = businessHours.weeklySchedule.find(
        (d) => d.day === dayName
      );

      if (!daySchedule || !daySchedule.isOpen) return ""; // today closed

      // Find the first interval that hasn't ended yet
      for (let interval of businessHours.timeSlots) {
        const start = time12ToMinutes(interval.start);
        const end = time12ToMinutes(interval.end);
        if (currentMinutes >= end) continue;
        const nextSlot =
          currentMinutes < start ? start : Math.ceil(currentMinutes / 15) * 15;
        if (nextSlot < end) {
          const h = Math.floor(nextSlot / 60);
          const m = nextSlot % 60;
          const period = h >= 12 ? "PM" : "AM";
          const displayHour = h % 12 || 12;
          return `${displayHour}:${m.toString().padStart(2, "0")} ${period}`;
        }
      }
      // All intervals are over – return first slot of tomorrow’s first interval
      // But that requires checking tomorrow's open day. For simplicity return empty.
      return "";
    };

    // ✅ FIX: Helper to build a consistent petService object from itemData +
    // serviceDetails, ensuring base prices are never confused with sale prices.
    const buildEditFormData = (itemData, serviceDetails, petDetailsResult) => {
      const durationValue = serviceDetails?.duration
        ? formatDuration(
            serviceDetails.duration,
            petDetailsResult?.size?.size_label
          )
        : itemData.duration;

      const appointmentDate = itemData.start_date
        ? typeof itemData.start_date === "string"
          ? itemData.start_date.split("T")[0]
          : new Date(itemData.start_date).toISOString().split("T")[0]
        : "";

      // ✅ Prefer the freshly fetched serviceDetails for all price fields.
      // itemData may store either mrp or sale price depending on save path,
      // so we never trust it as the "base" price.
      const mrpPrice =
        serviceDetails?.mrp_price ??
        serviceDetails?.basic_price ??
        itemData.mrp_price ??
        itemData.unit_price ??
        itemData.price ??
        0;

      const salePrice =
        itemData.sale_price ??
        serviceDetails?.sale_price ??
        mrpPrice;

      const basicPrice =
        itemData.basic_price ??
        serviceDetails?.basic_price ??
        mrpPrice;

      const discountPercentage =
        itemData.discount_percentage ??
        serviceDetails?.discount_percentage ??
        0;

      const discountAmount =
        itemData.discount_amount ??
        serviceDetails?.discount_amount ??
        0;

      return {
        pet_id: itemData.vendor_pet_id || itemData.pet_id,
        appointment_date: appointmentDate,
        appointment_start_time: convertTo12Hour(itemData.start_time),
        service_id: itemData.service_id,
        duration: durationValue,
        // Base / MRP fields — never overwritten by discount
        mrp_price: mrpPrice,
        basic_price: basicPrice,
        // Sale / discounted fields
        sale_price: salePrice,
        discount_percentage: discountPercentage,
        discount_amount: discountAmount,
        // Effective price used for display / totals
        price: salePrice,
        // Booking-level manual discount (separate from service discount)
        discount: itemData.discount || 0,
        gst: itemData.gst || 0,
        gst_amount: itemData.gst || 0,
        totalAmount: itemData.total_price || 0,
        appointment_to: itemData.employee_id || itemData.technician_id || "",
        tax_percentage:
          serviceDetails?.tax_percentage ?? itemData?.tax_percentage ?? 0,
        notes: itemData?.notes || "",
        price_surcharge:
          itemData.price_surcharge ||
          serviceDetails?.price_surcharge ||
          [],
        service_name: serviceDetails?.name || "",
        service_category_name:
          serviceDetails?.service_category?.service_category_name || "",
        service_type_name:
          serviceDetails?.service_category?.service_type_name ||
          serviceDetails?.service_category?.service_category_name ||
          "",
      };
    };

    // Load initial data and handle edit mode
    useEffect(() => {
      const init = async () => {
        if (
          isOpen &&
          editMode &&
          itemData &&
          Object.keys(itemData).length > 0 &&
          !fromCalendar &&
          !editDataFetched
        ) {
          // ✅ FIX: Always fetch fresh service details so we have correct
          // mrp_price / sale_price / discount info, regardless of what
          // itemData contains.
          const appointmentDate = itemData.start_date
            ? typeof itemData.start_date === "string"
              ? itemData.start_date.split("T")[0]
              : new Date(itemData.start_date).toISOString().split("T")[0]
            : "";
          const petDetailsResult = await fetchPetDetails(
            itemData.vendor_pet_id
          );
          const serviceDetails = await fetchServiceDetails(
            itemData.service_id,
            petDetailsResult,
            appointmentDate
          );

          const tempFormData = buildEditFormData(
            itemData,
            serviceDetails,
            petDetailsResult
          );

          if (isFromQueueWithOriginalTime && originalQueueTime) {
            tempFormData.appointment_date = originalQueueTime.date;
            tempFormData.appointment_start_time = originalQueueTime.time;
          }
          setPetService(tempFormData);
          setEditDataFetched(true);
          setUserManuallyChangedPet(false);
        } else if (
          fromCalendar &&
          itemData &&
          itemData.start_date &&
          itemData.start_time &&
          !editDataFetched
        ) {
          const appointmentDate = itemData.start_date
            ? typeof itemData.start_date === "string"
              ? itemData.start_date.split("T")[0]
              : new Date(itemData.start_date).toISOString().split("T")[0]
            : "";

          // ✅ FIX: Fetch service details so price fields are consistent.
          let serviceDetails = null;
          if (itemData.service_id) {
            serviceDetails = await fetchServiceDetails(
              itemData.service_id,
              petDetails
            );
          }

          const mrpPrice =
            serviceDetails?.mrp_price ??
            serviceDetails?.basic_price ??
            itemData.mrp_price ??
            itemData.unit_price ??
            itemData.price ??
            0;
          const salePrice =
            serviceDetails?.sale_price ?? itemData.sale_price ?? mrpPrice;

          const tempFormData = {
            pet_id: itemData.vendor_pet_id || itemData.pet_id,
            appointment_date: appointmentDate,
            appointment_start_time: convertTo12Hour(itemData.start_time),
            service_id: itemData.service_id,
            duration: serviceDetails?.duration
              ? formatDuration(
                  serviceDetails.duration,
                  petDetails?.size?.size_label
                )
              : itemData.duration,
            mrp_price: mrpPrice,
            basic_price:
              serviceDetails?.basic_price ?? itemData.basic_price ?? mrpPrice,
            sale_price: salePrice,
            discount_percentage:
              serviceDetails?.discount_percentage ??
              itemData.discount_percentage ??
              0,
            discount_amount:
              serviceDetails?.discount_amount ??
              itemData.discount_amount ??
              0,
            price: salePrice,
            discount: itemData.discount || 0,
            gst: itemData.gst || 0,
            gst_amount: itemData.gst || 0,
            totalAmount: itemData.total_price || 0,
            appointment_to:
              itemData.employee_id || itemData.technician_id || "",
            tax_percentage:
              serviceDetails?.tax_percentage ??
              itemData?.tax_percentage ??
              0,
            notes: itemData?.notes || "",
            service_name: serviceDetails?.name || "",
            service_category_name:
              serviceDetails?.service_category?.service_category_name || "",
            service_type_name:
              serviceDetails?.service_category?.service_type_name ||
              serviceDetails?.service_category?.service_category_name ||
              "",
          };
          setPetService(tempFormData);
          setEditDataFetched(true);
          setUserManuallyChangedPet(false);
        } else if (fromCalendar && itemData.service_id && !editDataFetched) {
          const petId = itemData.vendor_pet_id || itemData.pet_id;
          const petDetailsResult = petId ? await fetchPetDetails(petId) : null;
          const serviceDetails = await fetchServiceDetails(
            itemData.service_id,
            petDetailsResult || petDetails
          );
          if (!serviceDetails) return;

          // ✅ FIX: Use the serviceDetails price fields directly so the
          // discounted value never becomes the base.
          const mrpPrice =
            serviceDetails.mrp_price ??
            serviceDetails.basic_price ??
            serviceDetails.price ??
            0;
          const salePrice = serviceDetails.sale_price ?? mrpPrice;

          setPetService((prev) => ({
            ...prev,
            service_id: itemData.service_id,
            duration: formatDuration(
              serviceDetails?.duration,
              petDetails?.size?.size_label
            ),
            mrp_price: mrpPrice,
            basic_price: serviceDetails.basic_price ?? mrpPrice,
            sale_price: salePrice,
            discount_percentage: serviceDetails.discount_percentage ?? 0,
            discount_amount: serviceDetails.discount_amount ?? 0,
            price: salePrice,
            discount: itemData.discount || 0,
            gst: itemData.gst || 0,
            gst_amount: itemData.gst || 0,
            tax_percentage: serviceDetails.tax_percentage ?? 0,
            appointment_date:
              isFromQueueWithOriginalTime && originalQueueTime
                ? originalQueueTime.date
                : selectedSlotInfo?.date || prev.appointment_date,
            appointment_start_time:
              isFromQueueWithOriginalTime && originalQueueTime
                ? originalQueueTime.time
                : convertTo12Hour(itemData.start_time) ||
                  selectedSlotInfo?.startTime.toUpperCase() ||
                  "",
            service_name: serviceDetails.name || "",
            service_category_name:
              serviceDetails?.service_category?.service_category_name || "",
            service_type_name:
              serviceDetails?.service_category?.service_type_name ||
              serviceDetails?.service_category?.service_category_name ||
              "",
          }));
          setEditDataFetched(true);
          setUserManuallyChangedPet(false);
        } else if (fromCalendar && selectedSlotInfo && !editDataFetched) {
          setPetService((prev) => ({
            ...prev,
            appointment_date:
              isFromQueueWithOriginalTime && originalQueueTime
                ? originalQueueTime.date
                : selectedSlotInfo.date,
            appointment_start_time:
              isFromQueueWithOriginalTime && originalQueueTime
                ? originalQueueTime.time
                : selectedSlotInfo?.startTime?.toUpperCase(),
          }));
          setEditDataFetched(true);
          setUserManuallyChangedPet(false);
        }
      };

      if (
        isOpen &&
        !itemData.start_date &&
        !itemData.start_time &&
        !selectedSlotInfo?.date &&
        !selectedSlotInfo?.startTime
      ) {
        setPetService((prev) => ({
          ...prev,
          appointment_start_time: convertTo12Hour(getNextTimeSlot()),
          appointment_date: new Date().toISOString().split("T")[0],
        }));
      }
      init();
    }, [
      isOpen,
      itemData,
      fromCalendar,
      petDetails,
      selectedSlotInfo,
      isFromQueueWithOriginalTime,
      originalQueueTime,
      businessHours,
    ]);

    useEffect(() => {
      if (isPetDetails && (itemData?.pet_id || itemData?.vendor_pet_id)) {
        setIsPetDisabled(true);
        if (!petService.pet_id && (itemData.pet_id || itemData.vendor_pet_id)) {
          setPetService((prev) => ({
            ...prev,
            pet_id: itemData.pet_id || itemData.vendor_pet_id,
          }));
          fetchPetDetails(itemData.pet_id || itemData.vendor_pet_id);
        }
      }
    }, [isPetDetails, itemData, petService.pet_id]);

    useEffect(() => {
      if (
        pets?.length > 0 &&
        itemData &&
        !userManuallyChangedPet &&
        !petService.pet_id
      ) {
        const preferredId = itemData.vendor_pet_id || itemData.pet_id;
        let foundPet = null;
        if (preferredId) {
          foundPet = pets.find((pet) => pet._id === preferredId);
        }
        if (!foundPet && itemData.pet_name) {
          foundPet = pets.find(
            (pet) =>
              pet.name &&
              pet.name.toLowerCase() === itemData.pet_name.toLowerCase()
          );
        }
        if (foundPet) {
          setPetService((prev) => ({ ...prev, pet_id: foundPet._id }));
          fetchPetDetails(foundPet._id);
        }
      }
    }, [
      pets,
      itemData?.vendor_pet_id,
      itemData?.pet_id,
      itemData?.pet_name,
      petService.pet_id,
      userManuallyChangedPet,
    ]);

    useEffect(() => {
      if (staffs?.length > 0 && itemData && !petService.appointment_to) {
        let foundStaff = null;
        if (itemData.employee_id) {
          foundStaff = staffs.find(
            (staff) => staff._id === itemData.employee_id
          );
        }
        if (!foundStaff && itemData.employee_name) {
          foundStaff = staffs.find(
            (staff) =>
              staff.name &&
              staff.name.toLowerCase() === itemData.employee_name.toLowerCase()
          );
        }
        if (foundStaff) {
          setPetService((prev) => ({
            ...prev,
            appointment_to: foundStaff._id,
          }));
        }
      }
    }, [staffs, itemData]);

    useEffect(() => {
      if (
        petService.service_id &&
        serviceDetailData?.duration &&
        petDetails?.size?.size_label
      ) {
        const newDuration = formatDuration(
          serviceDetailData.duration,
          petDetails.size.size_label
        );
        if (newDuration !== petService.duration) {
          setPetService((prev) => ({ ...prev, duration: newDuration }));
        }
      }
    }, [
      petDetails?.size?.size_label,
      serviceDetailData?.duration,
      petService.service_id,
    ]);

    const fetchVendorClientPets = async () => {
      try {
        const [petsRes] = await Promise.all([
          httpService.get(`/vendorpet/getvendorclientpets/${clientId}`),
        ]);
        setPets(petsRes.data);
      } catch (error) {
        console.error("Error loading pet types or breeds", error);
      }
    };
    useEffect(() => {
      if (isOpen) {
        fetchVendorClientPets();
      }
    }, [isOpen, serviceTypeId, itemData]);

    const fetchServicesForPet = useCallback(async () => {
      const requestId = ++servicesRequestRef.current;
      try {
        const petType = petDetails?.pet_type?.name;
        const sizeLabel = petDetails?.size?.size_label?.toLowerCase() || null;
        const breedCoat = petDetails?.breed?.breed_coat?.toLowerCase() || null;
        const petTypeId = petDetails?.pet_type?._id || null;
        const appointmentDate = petService.appointment_date || null;

        const response = await httpService.post(
          `/vendorservice/getvendorservices`,
          {
            petType: petType || null,
            status: "active",
            size: sizeLabel,
            breedCoat: breedCoat,
            petTypeId: petTypeId,
            appointmentDate: appointmentDate,
          }
        );

        const filteredServices = response.data.filter((service) => {
          if (!service.pet_type) return false;
          const currentPetType = petType?.toLowerCase()?.trim();
          if (!currentPetType) return false;

          let servicePetTypes = [];
          if (Array.isArray(service.pet_type)) {
            servicePetTypes = service.pet_type
              .map((pt) => pt?.name?.toLowerCase()?.trim())
              .filter((name) => name);
          } else if (typeof service.pet_type === "string") {
            servicePetTypes = service.pet_type
              .toLowerCase()
              .split(",")
              .map((pt) => pt.trim());
          } else {
            return false;
          }

          return (
            servicePetTypes.includes("both") ||
            servicePetTypes.includes(currentPetType)
          );
        });

        if (requestId !== servicesRequestRef.current) return;

        setServices(filteredServices);

        if (!filteredServices.length) {
          toast.warn(
            `Service not added for the pet type ${petType}. Please add Service for pet Type ${petType} and try again!!`
          );
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    }, [petDetails, petService.appointment_date]);
    useEffect(() => {
      if (petDetails?._id) {
        fetchServicesForPet();
      }
    }, [
      petDetails?._id,
      serviceTypeId,
      petService.appointment_date,
      fetchServicesForPet,
    ]);

    const handleServiceSaved = async () => {
      setShowServiceDrawer(false);
      await fetchServicesForPet();
      toast.success("Service added successfully!");
    };

    function getMinutes(durationStr) {
      if (!durationStr) return 0;
      const match = durationStr.match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    }
    /**
     * Converts 12-hour time (e.g. "8:30 am") + duration to a 24-hour time range
     * @param {string} startTime12h - e.g. "8:30 am", "1:15 PM"
     * @param {number} durationMinutes - duration in minutes
     * @returns {string} - e.g. "08:30-09:30"
     */
    function getTimeRange(startTime12h, durationMinutes) {
      const match = startTime12h.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
      if (!match) throw new Error("Invalid time format. Use 'h:mm am/pm'");
      let [_, hour, minute, period] = match;
      hour = parseInt(hour, 10);
      minute = parseInt(minute, 10);
      period = period.toLowerCase();
      if (period === "pm" && hour !== 12) hour += 12;
      if (period === "am" && hour === 12) hour = 0;
      const startDate = new Date();
      startDate.setHours(hour, minute, 0, 0);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
      const formatTime = (date) => {
        const h = date.getHours().toString().padStart(2, "0");
        const m = date.getMinutes().toString().padStart(2, "0");
        return `${h}:${m}`;
      };
      return `${formatTime(startDate)}-${formatTime(endDate)}`;
    }

    useEffect(() => {
      const getAvailableVendorStaffs = async () => {
        const timeSlots =
          petService.appointment_start_time && petService.duration
            ? getTimeRange(
                petService.appointment_start_time,
                getMinutes(petService.duration)
              )
            : null;
        if (timeSlots === null) return;
        const payload = {
          date: petService.appointment_date || null,
          timeSlots: timeSlots,
          serviceCategoryTypeId: serviceTypeId,
          appointmentId: itemData.ref_id || null,
        };

        try {
          const response = await httpService.post(
            "/employees/getAvailableVendorEmployees",
            payload
          );
          const filteredStaffs = response.data.filter(
            (item) => item.is_available === true || item.is_available == null
          );
          setStaffs(filteredStaffs);
          if (isEmployee) {
            const loggedInEmployeeId = data?.user?.employee_id;
            const isStaffAvailable = filteredStaffs.some(
              (item) => item._id == loggedInEmployeeId
            );
            if (!isStaffAvailable) {
              toast.warn(
                "You are not available at the selected time. Please choose a different time slot!!"
              );
              setPetService((prev) => ({
                ...prev,
                appointment_to: "",
              }));
            } else {
              setPetService((prev) => ({
                ...prev,
                appointment_to: loggedInEmployeeId,
              }));
            }
          } else {
            if (filteredStaffs.length === 0 && timeSlots) {
              toast.warn("No Staff available for the selected time slot!!");
              setStaffAvailableMessage(
                "No Staff available for the selected time slot!! Please select a different timeslot."
              );
              setPetService((prev) => ({ ...prev, appointment_to: "" }));
            } else {
              if (petService.appointment_to != "") {
                const isSelectedStaffAvailable = filteredStaffs.some(
                  (item) => item._id == petService.appointment_to
                );
                if (!isSelectedStaffAvailable) {
                  setPetService((prev) => ({ ...prev, appointment_to: "" }));
                }
              }
              setStaffAvailableMessage("");
            }
          }
        } catch (error) {
          console.error("Error checking staff occupancy:", error);
        }
      };
      getAvailableVendorStaffs();
    }, [
      petService.appointment_date,
      petService.appointment_start_time,
      petService.duration,
    ]);

    const handlePetServiceChange = (field, value) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (field === "appointment_date") {
        const checkinDate = new Date(value);
        checkinDate.setHours(0, 0, 0, 0);
        if (checkinDate < minDate) {
          toast.error(
            allowPastBooking
              ? `You can book only up to ${maxPastDays} day(s) in the past.`
              : "Appointment Date cannot be in the past."
          );
          return false;
        }
        if (businessHours) {
          const dayName = new Date(value).toLocaleDateString("en-US", {
            weekday: "long",
          });
          const daySchedule = businessHours.weeklySchedule.find(
            (d) => d.day === dayName
          );
          if (daySchedule && !daySchedule.isOpen) {
            const special = businessHours.specialDays?.find(
              (d) => new Date(d.date).toISOString().split("T")[0] === value
            );
            if (special && special.isOpen && special.slots.length > 0) {
              // return true;
            } else {
              toast.error(
                `We are closed on  2${dayName}s. Please choose an active business day.`
              );
              return false;
            }
          }
          const special = businessHours.specialDays?.find(
            (d) => new Date(d.date).toISOString().split("T")[0] === value
          );
          if (special && !special.isOpen && special.slots.length === 0) {
            toast.error(
              `We are closed on ${value} (${special.label}). Please choose another date.`
            );
            return false;
          }
        }
      }

      if (
        !allowPastBooking &&
        field === "appointment_start_time" &&
        petService.appointment_date
      ) {
        const selectedDate = new Date(petService.appointment_date);
        if (selectedDate.setHours(0, 0, 0, 0) === today.getTime()) {
          const [selectedHour, selectedMinute] = value.split(":").map(Number);
          const combinedSelectedDateTime = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            selectedHour,
            selectedMinute
          );
          if (combinedSelectedDateTime < now) {
            toast.error(
              "Appointment time cannot be in the past for today's date."
            );
            return false;
          }
        }
      }

      setPetService((prev) => ({
        ...prev,
        [field]: value,
      }));
      return true;
    };

    // Inside the component render, derive slots for the currently chosen date:
    const currentSlots = React.useMemo(() => {
      return generateDynamicSlotsForDate(petService.appointment_date);
    }, [petService.appointment_date, businessHours]);

    const formatDuration = (durationData, petSize = null) => {
      if (!durationData) return "";
      let targetDuration = null;
      if (durationData.duration_based_on === "flat") {
        targetDuration = durationData.flat;
      } else {
        const sizeKey = petSize?.toLowerCase() || "flat";
        const validSizes = ["small", "medium", "large", "giant"];
        const selectedSize = validSizes.includes(sizeKey) ? sizeKey : "flat";
        targetDuration = durationData[selectedSize] || durationData.flat;
      }
      if (!targetDuration) return "";
      const { hour = 0, minutes = 0 } = targetDuration;
      if (hour === 0 && minutes === 0) return "";
      const parts = [];
      if (hour > 0) parts.push(`${hour} Hour${hour > 1 ? "s" : ""}`);
      if (minutes > 0) parts.push(`${minutes} Minute${minutes > 1 ? "s" : ""}`);
      return parts.join(" ");
    };

    const calculatePriceFromAPI = async (serviceType) => {
      const effectiveClientId =
        clientId ||
        itemData?.vendor_client_id ||
        itemData?.client_id ||
        null;
      if (!effectiveClientId || !petService.pet_id || !petService.service_id) {
        return;
      }
      try {
        const requestPayload = {
          client_id: effectiveClientId,
          pet_id: petService.pet_id,
          vendor_service_id: petService.service_id,
          appointment_date: petService.appointment_date || null,
        };

        const response = await httpService.post(
          `/vendorbooking/getPriceCalculation?service_type=${serviceType}`,
          requestPayload
        );

        if (response && response.data !== undefined) {
          let calculatedPrice = 0;
          let gstAmount = 0;
          let surchargeDetails = [];
          let discountAmount = 0;
          let discountPercentage = 0;
          let salePrice = 0;
          let mrpPrice = 0;
          let basicPrice = 0;

          if (typeof response.data === "number") {
            calculatedPrice = response.data;
            mrpPrice = response.data;
            salePrice = response.data;
            basicPrice = response.data;
          } else if (
            typeof response.data === "object" &&
            response.data !== null
          ) {
            // ✅ FIX: Read mrp_price / basic_price explicitly from the API
            // response if provided. Never let sale_price override them.
            mrpPrice =
              response.data.mrp_price ??
              response.data.basic_price ??
              response.data.price ??
              0;
            basicPrice =
              response.data.basic_price ?? response.data.mrp_price ?? mrpPrice;
            salePrice = response.data.sale_price ?? mrpPrice;
            calculatedPrice = salePrice;
            gstAmount =
              response.data.discounted_tax || response.data.taxAmount || 0;
            surchargeDetails = response.data.surchargeDetails || [];
            discountAmount = response.data.discount_amount || 0;
            discountPercentage = response.data.discount_percentage || 0;
          }

          const finalPrice = discountPercentage === 100 ? 0 : calculatedPrice;

          setPetService((prev) => ({
            ...prev,
            // Keep base prices separate and authoritative
            mrp_price: mrpPrice || prev.mrp_price,
            basic_price: basicPrice || prev.basic_price,
            sale_price: salePrice,
            price: finalPrice,
            price_surcharge: surchargeDetails,
            gst_amount: gstAmount,
            discount_amount: discountAmount,
            discount_percentage: discountPercentage,
            effective_price: finalPrice,
          }));

          setServiceDetailData((prev) =>
            prev && typeof prev === "object"
              ? {
                  ...prev,
                  discount_amount: discountAmount,
                  discount_percentage: discountPercentage,
                  sale_price: salePrice,
                  surchargeDetails: surchargeDetails,
                }
              : prev
          );
        }
      } catch (error) {
        console.error("Error calling price calculation API:", error);
      }
    };

    const fetchServiceDetails = async (
      serviceId,
      petDetailsOverride = null,
      appointmentDateOverride = null
    ) => {
      try {
        let currentPetDetails = petDetailsOverride || petDetails;

        if (
          (!currentPetDetails || Object.keys(currentPetDetails).length === 0) &&
          petService.pet_id
        ) {
          currentPetDetails = await fetchPetDetails(petService.pet_id);
        }

        const sizeLabel =
          currentPetDetails?.size?.size_label?.toLowerCase() || null;
        const breedCoat =
          currentPetDetails?.breed?.breed_coat?.toLowerCase() ||
          currentPetDetails?.breed?.breed_name?.toLowerCase() ||
          null;
        const petTypeId =
          currentPetDetails?.pet_type?._id ||
          currentPetDetails?.pet_type_id ||
          null;
        const appointmentDate =
          appointmentDateOverride || petService.appointment_date || null;

        // ✅ Use POST instead of GET
        const response = await httpService.post(
          `/vendorservice/getvendorservice/${serviceId}`,
          {
            size: sizeLabel,
            breedCoat: breedCoat,
            petType: petTypeId,
            appointmentDate: appointmentDate,
          }
        );

        const serviceDetailData = response.data;
        setServiceDetailData(serviceDetailData);

        // ✅ FIX: Explicitly separate base (mrp/basic) from sale price.
        // `price` is the effective price used for display/totals, but the
        // base price fields are preserved untouched so a subsequent save
        // never mistakes the discounted value for the base value.
        const mrpPrice =
          serviceDetailData.mrp_price ??
          serviceDetailData.basic_price ??
          0;
        const basicPrice =
          serviceDetailData.basic_price ?? serviceDetailData.mrp_price ?? 0;
        const salePrice = serviceDetailData.sale_price ?? mrpPrice;

        setPetService((prev) => ({
          ...prev,
          // Effective / sale price
          price: salePrice,
          sale_price: salePrice,
          // Base prices (authoritative, never overwritten by discount)
          basic_price: basicPrice,
          mrp_price: mrpPrice,
          discount_percentage: serviceDetailData.discount_percentage ?? 0,
          discount_amount: serviceDetailData.discount_amount ?? 0,
          tax_percentage: serviceDetailData.tax_percentage || 0,
          price_surcharge: [],
          duration: formatDuration(
            serviceDetailData?.duration,
            currentPetDetails?.size?.size_label
          ),
          service_name: serviceDetailData?.name || "",
          service_category_name:
            serviceDetailData?.service_category?.service_category_name || "",
          service_type_name:
            serviceDetailData?.service_category?.service_type_name ||
            serviceDetailData?.service_category?.service_category_name ||
            "",
        }));

        // Handle addons
        const selectedIds = new Set(
          itemData?.addons?.map((item) => item.addon_id) || []
        );
        const mapped = {};
        if (
          serviceDetailData.adon_services &&
          Array.isArray(serviceDetailData.adon_services)
        ) {
          serviceDetailData.adon_services.forEach((addon) => {
            mapped[addon.adon_id] = {
              selected: selectedIds.has(addon.adon_id),
              price: addon.value === "Free" ? 0 : parseFloat(addon.value),
              label: addon.name || "",
            };
          });
          setServicesd(mapped);
        }

        return serviceDetailData;
      } catch (error) {
        console.error("Failed to fetch service details:", error);
        toast.error("Failed to fetch service details");
        return null;
      }
    };

    const fetchPetDetails = async (petId) => {
      try {
        const response = await httpService.get(
          `/vendorpet/getvendorpet/${petId}`
        );
        const petDetail = response.data;
        setPetDetails(petDetail);
        return petDetail;
      } catch (error) {
        console.error("Failed to fetch pet details:", error);
        return null;
      }
    };

    useEffect(() => {
      if (itemData.pet_id) {
        fetchPetDetails(itemData.pet_id);
      }
    }, [itemData.pet_id]);

    // ✅ FIX: Recompute totals from the base / sale fields correctly.
    // The service's own discount (discount_percentage / discount_amount)
    // is already reflected in `sale_price`, so we must NOT subtract it twice.
    // `petService.discount` here is the *booking-level manual* discount.
    useEffect(() => {
      const salePrice =
        parseFloat(petService.sale_price || petService.price || "0") || 0;
      const manualDiscount = parseFloat(petService.discount || "0") || 0;
      const gstAmount = parseFloat(petService.gst_amount || "0") || 0;
      const addonsTotal = Object.values(servicesd || {}).reduce(
        (sum, addon) => {
          const addonPrice = parseFloat(addon.price);
          if (addon.selected && !isNaN(addonPrice)) {
            return sum + addonPrice;
          }
          return sum;
        },
        0
      );
      const subtotalRaw = salePrice + addonsTotal;
      const subtotal = Math.max(0, subtotalRaw);
      const total = Math.max(0, subtotal - manualDiscount);
      setPetService((prev) => ({
        ...prev,
        totalAmount: isNaN(total) ? "0.00" : total.toFixed(2),
      }));
    }, [
      petService.sale_price,
      petService.price,
      petService.discount,
      petService.gst_amount,
      servicesd,
      serviceDetailData,
    ]);

    const resetFormData = () => {
      setServiceDetailData([]);
      setEditDataFetched(false);
      setPetDetails([]);
      setShowNewPetForm(false);
      setUserManuallyChangedPet(false);
      setPetService({
        pet_id: "",
        size: "",
        breed: "",
        pet_type: "",
        appointment_date: "",
        appointment_start_time: "",
        service_id: "",
        duration: "",
        price: 0,
        basic_price: 0,
        mrp_price: 0,
        sale_price: 0,
        discount_percentage: 0,
        discount_amount: 0,
        discount: 0,
        gst: 0,
        gst_amount: 0,
        totalAmount: 0,
        appointment_to: "",
        tax_percentage: 0,
        notes: "",
        unit_price: 0,
        price_surcharge: [],
      });
      // Reset forms
      setSelectedForms([]);
      setAvailableForms([]);
      setSentFormNames([]);
      preselectedFormIdsRef.current = new Set();
    };

    const handleAddAppointment = async () => {
      try {
        if (!petService?.pet_id) {
          toast.warn("Please select a pet", { position: "top-right" });
          return;
        }
        if (!petService?.service_id) {
          toast.warn("Please select a service", { position: "top-right" });
          return;
        }

        // ✅ VALIDATION: Check if date and time are selected
        if (!petService?.appointment_date) {
          toast.warn("Please select appointment date", {
            position: "top-right",
          });
          return;
        }
        if (!petService?.appointment_start_time) {
          toast.warn("Please select appointment time", {
            position: "top-right",
          });
          return;
        }
        if (!petService?.duration) {
          toast.warn(
            "Service duration not set. Please select a valid service.",
            {
              position: "top-right",
            }
          );
          return;
        }

        if (isSizeRequired && !petDetails?.size?.size_label) {
          toast.warn("Pet size is required. Please update the pet details.", {
            position: "top-right",
          });
          return;
        }
        if (isCoatRequired && !petDetails?.breed?.breed_coat) {
          toast.warn(
            "Pet coat/breed is required. Please update the pet details.",
            {
              position: "top-right",
            }
          );
          return;
        }
        // Double-booking check (if provided)
        if (
          checkEmployeeDoubleBooking &&
          existingAppointments.length > 0 &&
          petService.appointment_to
        ) {
          const isEdit = editMode && itemData?._editIndex !== undefined;
          const editIndex = isEdit ? itemData._editIndex : -1;
          const selectedStaff = staffs.find(
            (staff) => staff._id === petService.appointment_to
          );
          const appointmentToCheck = {
            technician_id: petService.appointment_to,
            employee_name: selectedStaff?.name || "",
            start_date: petService.appointment_date,
            start_time: petService.appointment_start_time,
            pet_name: pets.find((p) => p._id === petService.pet_id)?.name || "",
            item_type: "grooming",
          };
          const validationResult = checkEmployeeDoubleBooking(
            existingAppointments,
            appointmentToCheck,
            editIndex
          );
          if (!validationResult.isValid) {
            toast.error(validationResult.conflictMessage, {
              position: "top-right",
              autoClose: 5000,
            });
            return;
          }
        }

        const selectedPet = pets.find((pet) => pet._id === petService.pet_id);
        const selectedService = services.find(
          (service) => service._id === petService.service_id
        );
        const selectedStaff = staffs.find(
          (staff) => staff._id === petService.appointment_to
        );
        const originalOrderType =
          itemData?.order_type || itemData?.orderType || "offline";

        // ✅ Collect selected form IDs and names
        const formIds = selectedForms.map((form) => form._id).filter(Boolean);
        const selectedFormNames = selectedForms
          .map((form) => form.form_name || form.name || "")
          .filter(Boolean);
        const hasMandatoryForm = selectedForms.some(
          (form) => getFormRequirement(form) === "mandatory"
        );

        // ✅ FIX: Always send the true base (mrp/basic) price as `price` and
        // `unit_price`, and the actual sale price separately. Using `??`
        // ensures 0 isn't skipped in favour of a discounted value.
        const basePrice =
          petService.mrp_price ??
          petService.basic_price ??
          petService.price ??
          0;
        const salePrice = petService.sale_price ?? petService.price ?? basePrice;

        const appointmentData = {
          pet_id: petService.pet_id,
          vendor_pet_id: petService.pet_id,
          pet_name: selectedPet?.name || "",
          pet_type: petDetails?.pet_type?.name || "",
          pet_size: petDetails?.size?.size_label || "",
          pet_breed: petDetails?.breed?.breed_name || "",
          pet_type_id: petDetails?.pet_type?._id || "",
          service_id: petService.service_id,
          service_name: selectedService?.name || "",
          service_category_type_id: serviceTypeId,
          service_type_name:
            selectedService?.service_category?.service_type_name || "Grooming",
          technician_id: petService.appointment_to,
          employee_id: petService.appointment_to,
          employee_name: selectedStaff?.name || "",
          start_date: petService.appointment_date,
          start_time: petService.appointment_start_time,
          duration: petService.duration,
          // Base / MRP price (authoritative)
          price: basePrice,
          unit_price: basePrice,
          mrp_price: basePrice,
          basic_price: petService.basic_price ?? basePrice,
          // Sale price (discounted)
          sale_price: salePrice,
          discount_percentage: petService.discount_percentage ?? 0,
          discount_amount: petService.discount_amount ?? 0,
          // Booking-level manual discount
          discount: petService.discount,
          gst: petService.gst_amount || 0,
          tax_percentage: petService.tax_percentage || 0,
          discounted_tax: petService.gst_amount || 0,
          total_price: petService.totalAmount,
          addons: itemData?.addons || [],
          item_type: "grooming",
          notes: petService.notes,
          price_surcharge: petService.price_surcharge,
          // ✅ Include form_ids and human-readable form names for UI display
          form_ids: formIds,
          form_names: selectedFormNames,
          form_name: selectedFormNames[0] || "",
          form_has_mandatory: hasMandatoryForm,
          ...(itemData?.order_type && { order_type: itemData.order_type }),
          ...(!itemData?._fromEnquiry &&
            itemData?.ref_id && { ref_id: itemData.ref_id }),
          ...(!itemData?._fromEnquiry && !itemData?.ref_id && itemData?._id
            ? { ref_id: itemData._id }
            : {}),
          ...(!itemData?._fromEnquiry &&
          !itemData?.ref_id &&
          !itemData?._id &&
          itemData?.id
            ? { ref_id: itemData.id }
            : {}),
        };

        // If we are editing an existing locally-added appointment in AddAppointment (calendar flow)
        // carry through the index markers so the parent can update its local list.
        if (itemData?._editIndex !== undefined) {
          appointmentData._editIndex = itemData._editIndex;
          if (itemData?._originalAppointment) {
            appointmentData._originalAppointment =
              itemData._originalAppointment;
          }
        }

        const apiPayload = {
          order_type: originalOrderType,
          vendor_client_id: clientId,
          order_items: [
            {
              ...appointmentData,
              ...(appointmentStatusRef?.current && {
                status: appointmentStatusRef.current,
              }),
            },
          ],
          ...(orderId && { order_id: orderId }),
          ...(itemData?.order_id && { order_id: itemData.order_id }),
        };

        try {
          if (fromCalendar) {
            if (onUpdate) onUpdate(appointmentData);
          } else {
            // ORDERS PAGE FLOW ----------------------------------------------
            // Check if this is being called from BookingDetailsDrawer vs Orders.js
            // BookingDetailsDrawer passes order_id in itemData or orderId prop
            // Orders.js doesn't pass order_id in itemData

            const isFromBookingDetailsDrawer = Boolean(itemData?.order_id);
            if (isFromBookingDetailsDrawer) {
              // We are inside BookingDetailsDrawer (edit/create) — persist immediately
              try {
                const response = await httpService.post(
                  "/vendororder/addVendorOrderOrUpdate",
                  apiPayload
                );
                if (onUpdate) onUpdate(response.data);
              } catch (error) {
                console.error("Error saving appointment:", error);
                toast.error("Failed to save appointment");
                return;
              }
            } else {
              // We are inside Orders.js — hand data back to parent so it can decide when to persist
              if (onUpdate) onUpdate(appointmentData);
            }
          }
        } catch (error) {
          console.error("Error saving appointment:", error);
          toast.error("Failed to save appointment");
          return;
        }

        resetFormData();
        onClose();
      } catch (error) {
        console.error("Error adding appointment:", error);
        toast.error("Failed to add appointment. Please try again", {
          position: "top-right",
        });
      }
    };

    const handleSaveAsEnquiry = async () => {
      await handleAddAppointment();
      setSaveEnquiryClicked?.(true);
    };

    // Scroll lock
    useEffect(() => {
      if (isOpen) {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
      } else {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      }
      return () => {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      };
    }, [isOpen]);

    // Effect to notify parent about pet form state changes
    useEffect(() => {
      if (onPetFormStateChange && renderAsContent) {
        onPetFormStateChange(showNewPetForm);
      }
    }, [showNewPetForm, onPetFormStateChange, renderAsContent]);

    // Effect to trigger price calculation API when both pet and service are selected
    useEffect(() => {
      if (petService.pet_id && petService.service_id && serviceDetailData) {
        calculatePriceFromAPI("grooming");
      }
    }, [petService.pet_id, petService.service_id, serviceDetailData]);

    // ---------- Helper for Forms UI (isFormSent) ----------
    const isFormSent = (form) => {
      const formName = form?.form_name || form?.formName || form?.name || "";
      if (!formName) return false;
      const normalized = normalizeConsentName(formName);
      return sentFormNames.some(
        (sentName) => normalizeConsentName(sentName) === normalized
      );
    };
    const filteredEmployeeStaffs = staffs?.filter((staff) => {
      if (
        !petService.appointment_date ||
        !petService.appointment_start_time ||
        !petService.duration
      ) {
        return true;
      }

      const editIndex =
        editMode && itemData?._editIndex !== undefined
          ? itemData._editIndex
          : -1;

      const newStart = timeToMinutes(petService.appointment_start_time);
      const newDuration = parseDurationToMinutes(petService.duration);
      const newEnd = newStart + newDuration;

      const isConflicting = existingAppointments.some((appt, idx) => {
        if (idx === editIndex) return false;

        if (
          appt.item_type?.toLowerCase() === "boarding" ||
          appt.item_type?.toLowerCase() === "day_care"
        ) {
          return false;
        }

        if (
          appt.technician_id === staff._id &&
          appt.start_date === petService.appointment_date
        ) {
          if (!appt.start_time || !appt.duration) return false;

          const existingStart = timeToMinutes(appt.start_time);
          const existingDuration = parseDurationToMinutes(appt.duration);
          const existingEnd = existingStart + existingDuration;

          return newStart < existingEnd && newEnd > existingStart;
        }

        return false;
      });

      return !isConflicting;
    });

    useEffect(() => {
      const hasSelection =
        petService.appointment_date &&
        petService.appointment_start_time &&
        petService.duration;

      if (hasSelection && filteredEmployeeStaffs.length === 0) {
        setStaffAvailableMessage(
          "No staff available for the selected time slot. Please choose a different time."
        );
      } else {
        setStaffAvailableMessage("");
      }
    }, [
      filteredEmployeeStaffs,
      petService.appointment_date,
      petService.appointment_start_time,
      petService.duration,
    ]);

    // ---------- Main Content ----------
    const mainContent = (
      <>
        {showNewPetForm ? (
          <AddNewPet
            isOpen={showNewPetForm}
            onClose={() => setShowNewPetForm(false)}
            onSave={(addedPetData) => {
              setNewPet({
                type: addedPetData.pet_type,
                name: addedPetData.name,
                gender: addedPetData.gender,
                size: addedPetData.size,
                breed: addedPetData.breed,
                age: addedPetData.age,
                birth_date: addedPetData.birthday,
                behavior_notes: addedPetData.behavior_notes,
                tags: addedPetData.tags,
              });
              setPetService((prev) => ({
                ...prev,
                pet_id: addedPetData._id,
              }));
              fetchPetDetails(addedPetData._id);
              fetchVendorClientPets();
            }}
            clientId={clientId}
            isFullWidth={true}
            shouldHideSaveAndAdd={true}
            petId={isEditPet ? petDetails._id : null}
            existingPets={pets || []}
          />
        ) : (
          <Box
            sx={{ height: "100vh", display: "flex", flexDirection: "column" }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1000,
                backgroundColor: "#fff",
                px: 2,
                py: 1.5,
                borderBottom: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {!renderAsContent && (
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  <FiArrowLeft className="w-5 h-5 text-gray-700" />
                </button>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                px: 2,
                pb: 10,
              }}
            >
              {!itemData?.ref_id && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <PetsIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                    Pet <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Box display="flex" gap={1}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={petService.pet_id}
                      onChange={(e) => {
                        if (isPetDisabled) return;
                        const petId = e.target.value;
                        setPetService((prev) => ({
                          ...prev,
                          pet_id: petId,
                          petDetails: null,
                        }));
                        fetchPetDetails(petId);
                      }}
                      disabled={isPetDisabled}
                      placeholder={
                        isPetDisabled ? "Pet pre-selected" : "Select pet"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PetsIcon
                              sx={{
                                color: isPetDisabled ? "#9ca3af" : "#6b7280",
                                fontSize: 18,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: isPetDisabled ? "#f9fafb" : "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: isPetDisabled ? "#e5e7eb" : "#3b82f6",
                            boxShadow: isPetDisabled
                              ? "none"
                              : "0 4px 12px rgba(59, 130, 246, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: isPetDisabled ? "#e5e7eb" : "#3b82f6",
                            boxShadow: isPetDisabled
                              ? "none"
                              : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "#374151",
                        },
                      }}
                    >
                      {isPetDisabled && petService.pet_id ? (
                        <MenuItem value={petService.pet_id}>
                          {(() => {
                            const selectedPet = pets.find(
                              (pet) => pet._id === petService.pet_id
                            );
                            if (selectedPet) {
                              const name = selectedPet?.name ?? null;
                              const breedName =
                                selectedPet?.breed?.breed_name ?? null;
                              const age =
                                selectedPet?.age &&
                                selectedPet.age.toString().trim() !== ""
                                  ? `${selectedPet.age} yrs`
                                  : null;
                              const sizeLabel =
                                selectedPet?.size_details?.size_label ??
                                selectedPet?.size?.size_label ??
                                null;
                              const leftChunk = [name, breedName]
                                .filter(Boolean)
                                .join(" - ");
                              const pieces = [];
                              if (leftChunk) {
                                pieces.push(
                                  age ? `${leftChunk} (${age})` : leftChunk
                                );
                              } else if (age) {
                                pieces.push(age);
                              }
                              if (sizeLabel)
                                pieces.push(sizeLabel.charAt(0).toUpperCase());
                              return pieces.join(" | ");
                            }
                            return "Selected Pet";
                          })()}
                        </MenuItem>
                      ) : (
                        pets
                          .filter(
                            (pet) => pet.status?.toLowerCase() !== "inactive"
                          )
                          ?.map((pet) => (
                            <MenuItem key={pet._id} value={pet._id}>
                              {(() => {
                                const name = pet?.name ?? null;
                                const breedName =
                                  pet?.breed?.breed_name ?? null;
                                const age =
                                  pet?.age && pet.age.toString().trim() !== ""
                                    ? `${pet.age} yrs`
                                    : null;
                                const sizeLabel =
                                  pet?.size_details?.size_label ??
                                  pet?.size?.size_label ??
                                  null;
                                const leftChunk = [name, breedName]
                                  .filter(Boolean)
                                  .join(" - ");
                                const pieces = [];
                                if (leftChunk) {
                                  pieces.push(
                                    age ? `${leftChunk} (${age})` : leftChunk
                                  );
                                } else if (age) {
                                  pieces.push(age);
                                }
                                if (sizeLabel)
                                  pieces.push(
                                    sizeLabel.charAt(0).toUpperCase()
                                  );
                                return pieces.join(" | ");
                              })()}
                            </MenuItem>
                          ))
                      )}
                    </TextField>
                    {!isPetDisabled && (
                      <>
                        {petDetails?._id && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setIsEditPet(true);
                              setShowNewPetForm(true);
                            }}
                            sx={{
                              minWidth: "60px",
                              height: "40px",
                              textTransform: "none",
                              borderRadius: "12px",
                              background:
                                "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                              border: "none",
                              color: "white",
                              fontWeight: 600,
                              fontSize: "13px",
                              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                boxShadow:
                                  "0 6px 16px rgba(99, 102, 241, 0.35)",
                                transform: "translateY(-1px)",
                                border: "none",
                              },
                              "& .MuiButton-startIcon": {
                                marginRight: "6px",
                                "& svg": { fontSize: "16px" },
                              },
                            }}
                            style={{
                              display: isPetDisabled ? "none" : "inline-flex",
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsEditPet(false);
                            setShowNewPetForm(true);
                          }}
                          sx={{
                            minWidth: "100px",
                            height: "40px",
                            textTransform: "none",
                            borderRadius: "12px",
                            background:
                              "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "13px",
                            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                              boxShadow: "0 6px 16px rgba(99, 102, 241, 0.35)",
                              transform: "translateY(-1px)",
                              border: "none",
                            },
                            "& .MuiButton-startIcon": {
                              marginRight: "6px",
                              "& svg": { fontSize: "16px" },
                            },
                          }}
                          startIcon={<MdPets />}
                          style={{
                            display: isPetDisabled ? "none" : "inline-flex",
                          }}
                        >
                          Add Pet
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              )}

              {petDetails.length != 0 && (
                <Grid item xs={12}>
                  <PetCard
                    pet={petDetails}
                    onEdit={() => {
                      setIsEditPet(true);
                      setShowNewPetForm(true);
                    }}
                    serviceName={
                      serviceDetailData?.service_category
                        ?.service_category_name ||
                      petService.service_category_name ||
                      ""
                    }
                  />
                </Grid>
              )}

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {isFromQueueWithOriginalTime && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "primary.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "primary.200",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          Using Original Queue Schedule
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This appointment is scheduled for its original queue
                          time: {originalQueueTime?.date} at{" "}
                          {originalQueueTime?.time}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!isFromQueueWithOriginalTime}
                            onChange={(e) => {
                              setIsFromQueueWithOriginalTime(!e.target.checked);
                            }}
                          />
                        }
                        label="Edit Time"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <FiCalendar className="w-4 h-4 text-blue-600" />
                    Date <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <div className="relative">
                    <input
                      type="date"
                      lang="en-IN"
                      value={petService.appointment_date}
                      onChange={(e) => {
                        if (isFromQueueWithOriginalTime) return;
                        const newValue = e.target.value;
                        const previousValue = petService.appointment_date;
                        const isValid = handlePetServiceChange(
                          "appointment_date",
                          newValue
                        );
                        if (!isValid) {
                          e.target.value = previousValue || "";
                        }
                      }}
                      disabled={isFromQueueWithOriginalTime}
                      min={minDateStr}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm ${
                        isFromQueueWithOriginalTime
                          ? "bg-gray-50 border-gray-200"
                          : "bg-white border-gray-200 hover:border-blue-400"
                      }`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiCalendar className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <FiClock className="w-4 h-4 text-green-600" />
                    Time Slot <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <div className="relative">
                    {isFromQueueWithOriginalTime ? (
                      <input
                        type="text"
                        value={petService.appointment_start_time}
                        readOnly
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                      />
                    ) : (
                      <select
                        value={petService.appointment_start_time}
                        onChange={(e) => {
                          handlePetServiceChange(
                            "appointment_start_time",
                            e.target.value
                          );
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white hover:border-green-400 text-sm appearance-none"
                      >
                        <option value="">Select time</option>

                        {(() => {
                          // 1. Filter out past slots (existing behavior)
                          const availableSlots = currentSlots.filter(
                            (slot) =>
                              !isPastTimeSlot(
                                slot,
                                petService.appointment_date
                              )
                          );

                          // 2. Ensure the currently selected time is ALWAYS in the list,
                          //    even if it's a past time (so it displays correctly).
                          const selectedTime =
                            petService.appointment_start_time;
                          if (
                            selectedTime &&
                            !availableSlots.includes(selectedTime)
                          ) {
                            // Insert it and keep the list sorted chronologically
                            availableSlots.push(selectedTime);
                            availableSlots.sort(
                              (a, b) => timeToMinutes(a) - timeToMinutes(b)
                            );
                          }

                          return availableSlots.map((slot) => (
                            <option key={slot} value={slot}>
                              {slot}
                            </option>
                          ));
                        })()}
                      </select>
                    )}
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiClock className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    className="flex items-center gap-2"
                  >
                    <PetsIcon sx={{ fontSize: 16, color: "#10b981" }} />
                    Service <span style={{ color: "red" }}>*</span>
                  </Typography>
                  {serviceDetailData && petService.service_id && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowServiceDetailsModal(true)}
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        color: "#6b7280",
                        "&:hover": { color: "#3b82f6" },
                      }}
                    >
                      <FiEye size={18} />
                      <Typography variant="caption" sx={{ ml: 0.5 }}>
                        View Details
                      </Typography>
                    </Button>
                  )}
                </Box>

                {/* Inline Flexbox Container for Dropdown + Add Service Button */}
                <Box display="flex" gap={1} alignItems="center" sx={{ mb: 2 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={petService.service_id}
                    onChange={async (e) => {
                      const serviceId = e.target.value;
                      setPetService((prev) => ({
                        ...prev,
                        service_id: serviceId,
                      }));
                      let currentPetDetails = petDetails;
                      if (
                        (!currentPetDetails ||
                          Object.keys(currentPetDetails).length === 0) &&
                        petService.pet_id
                      ) {
                        currentPetDetails = await fetchPetDetails(
                          petService.pet_id
                        );
                      }
                      if (serviceId) {
                        await fetchServiceDetails(serviceId);
                      }
                      setShowServiceDetailsModal(false);
                    }}
                    SelectProps={{
                      MenuProps: {
                        style: { zIndex: 99999 },
                        disablePortal: false,
                      },
                    }}
                    placeholder="Select service"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PetsIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#10b981",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#10b981",
                          boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                        },
                      },
                    }}
                  >
                    {services && services.length > 0 ? (
                      services.map((service) => {
                        const mrpPrice = Number(
                          service.mrp_price ||
                            service.basic_price ||
                            service.price ||
                            0
                        );
                        const salePrice = Number(
                          service.sale_price ||
                            service.basic_price ||
                            service.price ||
                            0
                        );

                        // Check if discount is 100%
                        const isFree = service.discount_percentage === 100;
                        const hasDiscount = salePrice < mrpPrice || isFree;

                        // Display price: if free, show ₹0 or FREE text
                        const displayPrice = isFree ? 0 : salePrice;

                        return (
                          <MenuItem
                            key={service._id}
                            value={service._id}
                            sx={{
                              py: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>{service?.name}</span>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {hasDiscount ? (
                                <>
                                  <Typography
                                    component="span"
                                    sx={{
                                      textDecoration: "line-through",
                                      color: "#9ca3af",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ₹{mrpPrice}
                                  </Typography>
                                  {isFree ? (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#10b981",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                      }}
                                    >
                                      FREE
                                    </Typography>
                                  ) : (
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#10b981",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                      }}
                                    >
                                      ₹{displayPrice}
                                    </Typography>
                                  )}
                                  {service.discount_percentage > 0 && (
                                    <Chip
                                      label={(() => {
                                        const discountType =
                                          service.applied_discount?.type;
                                        const discountValue =
                                          service.applied_discount?.value;
                                        if (discountType === "FLAT") {
                                          return `₹${discountValue} OFF`;
                                        } else {
                                          return `${service.discount_percentage}% OFF`;
                                        }
                                      })()}
                                      size="small"
                                      sx={{
                                        backgroundColor: isFree
                                          ? "#dcfce7"
                                          : "#fef3c7",
                                        color: isFree ? "#166534" : "#d97706",
                                        fontSize: "10px",
                                        height: "20px",
                                        "& .MuiChip-label": { px: 1 },
                                      }}
                                    />
                                  )}
                                </>
                              ) : (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "#10b981",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                  }}
                                >
                                  ₹{mrpPrice}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem value="" disabled>
                        No services available
                      </MenuItem>
                    )}
                  </TextField>

                  {/* Added Inline Button */}
                  {!isEmployee && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setShowServiceDrawer(true);
                      }}
                      startIcon={<AddIcon />}
                      sx={{
                        minWidth: "130px",
                        height: "40px",
                        textTransform: "none",
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "13px",
                        boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #059669 0%, #047857 100%)",
                          boxShadow: "0 6px 16px rgba(16, 185, 129, 0.35)",
                          transform: "translateY(-1px)",
                        },
                        "& .MuiButton-startIcon": {
                          marginRight: "4px",
                          "& svg": { fontSize: "18px" },
                        },
                      }}
                    >
                      Add Service
                    </Button>
                  )}
                </Box>
              </Grid>

              {serviceDetailData &&
                Object.keys(serviceDetailData).length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      background:
                        "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      border: "1px solid #e2e8f0",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                        justifyContent: "flex-start",
                        mb: 1,
                        pb: 1,
                        borderBottom: "1px dashed #cbd5e1",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#475569" }}>
                        <strong>Duration:</strong>{" "}
                        {petService.duration || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                        |
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#475569" }}>
                        <strong>Service charge:</strong>{" "}
                        {petService.tax_percentage || 0}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#475569" }}>
                        <strong>Base price:</strong> ₹
                        {serviceDetailData.basic_price ||
                          petService.basic_price ||
                          petService.mrp_price ||
                          0}
                      </Typography>
                      {(petService.price_surcharge?.find(
                        (s) => s.name === "coat_surcharge"
                      ) ||
                        petService.price_surcharge?.find(
                          (s) => s.name === "size_surcharge"
                        )) && (
                        <>
                          <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                            |
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            <strong>Surcharge:</strong>{" "}
                            {petService.price_surcharge?.find(
                              (s) => s.name === "coat_surcharge"
                            ) && (
                              <span>
                                Coat: ₹
                                {petService.price_surcharge.find(
                                  (s) => s.name === "coat_surcharge"
                                )?.price || 0}
                              </span>
                            )}
                            {petService.price_surcharge?.find(
                              (s) => s.name === "coat_surcharge"
                            ) &&
                              petService.price_surcharge?.find(
                                (s) => s.name === "size_surcharge"
                              ) &&
                              " + "}
                            {petService.price_surcharge?.find(
                              (s) => s.name === "size_surcharge"
                            ) && (
                              <span>
                                Size: ₹
                                {petService.price_surcharge.find(
                                  (s) => s.name === "size_surcharge"
                                )?.price || 0}
                              </span>
                            )}
                          </Typography>
                        </>
                      )}
                      {petService.discount_percentage > 0 && (
                        <>
                          <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                            |
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#475569" }}>
                            <strong>Discount:</strong>{" "}
                            {(() => {
                              const discountType =
                                petService?.applied_discount?.type ||
                                serviceDetailData?.applied_discount?.type;
                              const discountAmount =
                                petService.discount_amount ??
                                serviceDetailData?.discount_amount ??
                                0;

                              if (discountType === "FLAT") {
                                return `₹${discountAmount}`;
                              } else {
                                return `${petService.discount_percentage}% (₹${discountAmount})`;
                              }
                            })()}
                          </Typography>
                        </>
                      )}
                      <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                        |
                      </Typography>
                      {petService.discount_percentage === 100 ? (
                        <Typography
                          variant="body2"
                          sx={{ color: "#10b981", fontWeight: "bold" }}
                        >
                          <strong>Amount:</strong> FREE
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "#475569", fontWeight: "bold" }}
                        >
                          <strong>Amount:</strong> ₹
                          {(() => {
                            const amt =
                              petService.sale_price ||
                              petService.price ||
                              0;
                            const num = parseFloat(amt) || 0;
                            return num % 1 === 0 ? num : num.toFixed(2);
                          })()}
                          {petService.tax_percentage > 0 && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                color: "#475569",
                                fontWeight: "bold",
                                ml: 0.5,
                              }}
                            >
                              (+ ₹{petService.gst_amount.toFixed(2)} includes taxes)
                            </Typography>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

              {!isEmployee && (
                <Grid item xs={12} mb={2}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <PersonIcon sx={{ fontSize: 16, color: "#6366f1" }} />
                    Choose Staff
                    <span className="italic text-xs font-light text-gray-500">
                      {staffAvailableMessage}
                    </span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={petService.appointment_to}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPetService((prev) => ({
                        ...prev,
                        appointment_to: value,
                      }));
                    }}
                    SelectProps={{
                      MenuProps: {
                        style: { zIndex: 99999 },
                        disablePortal: false,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#6366f1",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6366f1",
                          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  >
                    {/* Placeholder option — clickable so staff can be cleared */}
                    <MenuItem
                      value=""
                      disabled={filteredEmployeeStaffs.length === 0}
                    >
                      {filteredEmployeeStaffs.length === 0
                        ? "No staff available for this time slot"
                        : "Select staff member"}
                    </MenuItem>

                    {/* Use precomputed filteredEmployeeStaffs */}
                    {filteredEmployeeStaffs.map((type) => (
                      <MenuItem key={type._id} value={type._id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.5}
                  className="flex items-center gap-2"
                  sx={{ fontSize: "15px" }}
                  onClick={() => setShowServiceNotes(!showServiceNotes)}
                  startIcon={
                    showServiceNotes ? (
                      <CancelIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <AddIcon sx={{ fontSize: 16 }} />
                    )
                  }
                >
                  {showServiceNotes ? "Cancel Notes" : "Add Service Notes"}
                </Button>
                {showServiceNotes && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={petService.notes}
                    onChange={(e) =>
                      handlePetServiceChange("notes", e.target.value)
                    }
                    placeholder="Add any special instructions or notes for this service..."
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        "&:hover": {
                          borderColor: "#6b7280",
                          boxShadow: "0 4px 12px rgba(13, 15, 19, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6b7280",
                          boxShadow: "0 0 0 3px rgba(107, 114, 128, 0.1)",
                        },
                      },
                    }}
                  />
                )}
              </Grid>

              {/* ========== FORMS SECTION (integrated) ========== */}
              <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.5}
                  className="flex items-center gap-2"
                  sx={{ fontSize: "17px" }}
                >
                  <NotesIcon sx={{ fontSize: 16, color: "#14b8a6" }} />
                  Forms
                </Typography>

                <Autocomplete
                  multiple
                  openOnFocus
                  clearOnBlur={false}
                  // ✅ Only filter by search text — do NOT exclude selected items.
                  // Excluding selected items from `options` breaks MUI's internal value
                  // tracking and prevents optional forms from being selected.
                  filterOptions={(options, state) => {
                    const input = String(state.inputValue || "")
                      .trim()
                      .toLowerCase();
                    if (!input) return options;
                    return options.filter((option) =>
                      String(option?.form_name || "")
                        .toLowerCase()
                        .includes(input)
                    );
                  }}
                  // ✅ Disable mandatory forms in the dropdown so they can't be deselected.
                  getOptionDisabled={(option) =>
                    getFormRequirement(option) === "mandatory"
                  }
                  loading={loadingForms}
                  options={availableForms}
                  value={selectedForms}
                  inputValue={formInputValue}
                  onInputChange={(event, newInputValue) =>
                    setFormInputValue(newInputValue)
                  }
                  onChange={(event, newValue) => {
                    // ✅ Mandatory forms are always locked (getOptionDisabled prevents
                    // MUI from removing them via the dropdown). But in case MUI strips
                    // them anyway (e.g. on clear), we always re-inject them here.
                    const mandatoryForms = availableForms.filter(
                      (f) => getFormRequirement(f) === "mandatory"
                    );
                    const mandatoryIds = new Set(
                      mandatoryForms.map((f) => f._id)
                    );

                    const merged = [
                      ...mandatoryForms,
                      ...(newValue || []).filter(
                        (f) => !mandatoryIds.has(f._id)
                      ),
                    ];

                    // Deduplicate by _id
                    const unique = Array.from(
                      new Map(
                        merged.filter(Boolean).map((item) => [item._id, item])
                      ).values()
                    );
                    setSelectedForms(unique);
                    setFormInputValue("");
                  }}
                  getOptionLabel={(option) => option.form_name || ""}
                  isOptionEqualToValue={(option, value) => {
                    if (option?._id && value?._id)
                      return option._id === value._id;
                    return (
                      String(option?.form_name || "").trim() ===
                      String(value?.form_name || "").trim()
                    );
                  }}
                  groupBy={(option) => {
                    if (option.category) {
                      return typeof option.category === "string"
                        ? option.category
                        : option.category.name || "No Category";
                    }
                    if (option.category_name) return option.category_name;
                    return "Select Forms";
                  }}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      const isMandatory =
                        getFormRequirement(option) === "mandatory";
                      const sent = isFormSent(option);

                      const chipSx = isMandatory
                        ? {
                            borderRadius: "16px",
                            backgroundColor: "#fee2e2",
                            border: "1px solid #fecaca",
                            height: "32px",
                            "& .MuiChip-label": {
                              fontSize: "14px",
                              color: "#b91c1c",
                            },
                          }
                        : {
                            borderRadius: "16px",
                            backgroundColor: "#ffedd5",
                            border: "1px solid #fed7aa",
                            height: "32px",
                            "& .MuiChip-label": {
                              fontSize: "14px",
                              color: "#b45309",
                            },
                          };

                      return (
                        <Chip
                          variant="outlined"
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <span>{option.form_name}</span>
                              {sent && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 0.75,
                                    py: 0.15,
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#166534",
                                    backgroundColor: "#dcfce7",
                                    border: "1px solid #bbf7d0",
                                  }}
                                >
                                  Sent
                                </Box>
                              )}
                            </Box>
                          }
                          key={option._id}
                          {...tagProps}
                          // ✅ Hide delete icon for mandatory forms
                          onDelete={isMandatory ? undefined : tagProps.onDelete}
                          sx={chipSx}
                        />
                      );
                    })
                  }
                  renderOption={(props, option) => {
                    const isMandatory =
                      getFormRequirement(option) === "mandatory";
                    const sent = isFormSent(option);
                    const badgeSx = isMandatory
                      ? {
                          backgroundColor: "#fee2e2",
                          color: "#b91c1c",
                          border: "1px solid #fecaca",
                        }
                      : {
                          backgroundColor: "#ffedd5",
                          color: "#b45309",
                          border: "1px solid #fed7aa",
                        };
                    return (
                      <li {...props} key={option._id || option.form_name}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1.5,
                            py: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {option.form_name}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1,
                                py: 0.2,
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: 700,
                                ...badgeSx,
                              }}
                            >
                              {isMandatory ? "Mandatory" : "Optional"}
                            </Box>
                          </Box>
                          {sent && (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1,
                                py: 0.2,
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#166534",
                                backgroundColor: "#dcfce7",
                                border: "1px solid #bbf7d0",
                              }}
                            >
                              Sent
                            </Box>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        selectedForms.length === 0
                          ? "Search and select forms..."
                          : ""
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "#10b981",
                            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#10b981",
                            boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                          },
                        },
                      }}
                    />
                  )}
                />

                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Send Selected Forms button (disabled by default) */}
                </Box>
              </Grid>
              {/* ========== END FORMS SECTION ========== */}
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                px: 2,
                py: 1.5,
                borderTop: "1px solid #eee",
                boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
                zIndex: 1000,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      resetFormData();
                      onClose();
                    }}
                    sx={{
                      py: 1.4,
                      fontWeight: "bold",
                      fontSize: "16px",
                      borderRadius: "12px",
                      textTransform: "none",
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  {!editMode && (
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<SaveIcon />}
                      onClick={handleSaveAsEnquiry}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                        boxShadow: 2,
                        backgroundColor: "#7E5FF5",
                        "&:hover": { backgroundColor: "#6D4EE5" },
                      }}
                    >
                      Save Enquiry
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {editMode ? (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={
                        (isEmployee
                          ? petService.appointment_to === "" ||
                            !petDetails.size?.size_label
                          : filteredEmployeeStaffs.length === 0 ||
                            !petDetails.size?.size_label) ||
                        (isSizeRequired && !petDetails?.size?.size_label) ||
                        (isCoatRequired && !petDetails?.breed?.breed_coat)
                      }
                      startIcon={<SaveIcon />}
                      onClick={handleAddAppointment}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                        boxShadow: 2,
                        "&:hover": { backgroundColor: "primary.dark" },
                      }}
                    >
                      Update
                    </Button>
                  ) : fromCalendar ? (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<AddIcon />}
                      disabled={
                        (isEmployee &&
                          (petService.appointment_to === "" ||
                            !petDetails.size?.size_label)) ||
                        (isSizeRequired && !petDetails?.size?.size_label) ||
                        (isCoatRequired && !petDetails?.breed?.breed_coat)
                      }
                      onClick={handleAddAppointment}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                        boxShadow: 2,
                        "&:hover": { backgroundColor: "primary.dark" },
                      }}
                    >
                      Add
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<SaveIcon />}
                      onClick={handleAddAppointment}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                        boxShadow: 2,
                        "&:hover": { backgroundColor: "primary.dark" },
                      }}
                    >
                      Save
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
        {/* Service Details Modal */}
        {showServiceDetailsModal && serviceDetailData && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
            onClick={() => setShowServiceDetailsModal(false)}
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "20px",
                maxWidth: 400,
                width: "100%",
                maxHeight: "80vh",
                overflow: "auto",
                p: 3,
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "#1f2937" }}
                >
                  {serviceDetailData?.name || "Service Details"}
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setShowServiceDetailsModal(false)}
                  sx={{ minWidth: "auto", p: 0.5, color: "#6b7280" }}
                >
                  ✕
                </Button>
              </Box>
              {serviceDetailData.applied_discount &&
                serviceDetailData.discount_amount > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                      border: "1px solid #f59e0b",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <ReceiptIcon sx={{ fontSize: 18, color: "#d97706" }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: "#92400e" }}
                      >
                        Discount Applied
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#78350f", mb: 0.5 }}
                    >
                      🎉{" "}
                      <strong>
                        {serviceDetailData.applied_discount?.name || "Discount"}
                      </strong>
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#78350f" }}>
                        Discount Amount:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: "#d97706" }}
                      >
                        -₹{serviceDetailData.discount_amount}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#78350f" }}>
                        Final Price:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: "#d97706" }}
                      >
                        ₹{serviceDetailData.sale_price}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#78350f", mt: 1, display: "block" }}
                    >
                      {serviceDetailData.applied_discount?.type === "FLAT"
                        ? `✨ You saved ₹${serviceDetailData.discount_amount} on this service!`
                        : `✨ You saved ${serviceDetailData.discount_percentage}% (₹${serviceDetailData.discount_amount}) on this service!`}
                    </Typography>
                  </Box>
                )}
              {serviceDetailData?.description && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ color: "#6b7280", mb: 0.5 }}
                  >
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: "#374151" }}>
                    {serviceDetailData.description}
                  </Typography>
                </>
              )}
              {serviceDetailData?.service_inclusions?.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ color: "#6b7280", mb: 0.5 }}
                  >
                    What's Included
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {serviceDetailData.service_inclusions.map(
                      (inclusion, idx) => (
                        <Box key={idx} sx={{ mb: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: "#374151" }}
                          >
                            {inclusion.inclusion_headings}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#6b7280", ml: 2 }}
                          >
                            {inclusion.selected_options?.join(", ") || "None"}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </>
    );

    return (
      <>
        {renderAsContent ? (
          mainContent
        ) : (
          <Drawer
            anchor="right"
            open={isOpen}
            onClose={() => {
              resetFormData();
              onClose();
            }}
            ModalProps={{ style: { zIndex: 10000 } }}
            PaperProps={{
              sx: {
                width: isMobile ? "100%" : "70vw",
                maxWidth: 800,
                p: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowX: "hidden",
              },
            }}
          >
            {mainContent}
          </Drawer>
        )}

        {/* ✅ Portal for ServiceDrawer – mounted at document.body */}
        {showServiceDrawer && (
          <>
            {createPortal(
              <ServiceDrawer
                isOpen={showServiceDrawer}
                onClose={() => setShowServiceDrawer(false)}
                onSaved={handleServiceSaved}
                serviceId={null}
              />,
              document.body
            )}
          </>
        )}
      </>
    );
  }
);

NewServiceDrawer.displayName = "NewServiceDrawer";

export default NewServiceDrawer;
